import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Table, message } from 'antd';

import DefaultButton from 'components/button/DefaultButton';
import UploadInput from 'components/input/UploadInput';
import LicenseEdit from 'components/table/LicenseEdit';
import AccountEdit from 'components/table/AccountEdit';
import EditData from 'components/table/EditData';
import AddressModal from 'components/modal/AddressModal';
import Viewer from 'components/layer/Viewer';

import { QueryKeys, restfetcher, getClient } from 'queryClient';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';

import { useAtomValue, useSetAtom } from 'jotai';
import modalInformationAtom from 'atom/modalInformationAtom';
import { extractDate, phoneAutoHyphen } from 'utils/common';

import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import { ReactComponent as IconCopy } from 'asserts/icons/ic_copy_lined.svg';
import companyInforAtom from 'atom/companyInfoAtom';
import RadioButton from 'components/button/RadioButton';
import { certificateTwoData } from 'consts/join';

interface BizDataParams {
  bizInfo: BizInfoTypes;
  authInfoApi: UseQueryResult<any, unknown>;
}

const BizInfo = ({ bizInfo, authInfoApi }: BizDataParams) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [openPostcode, setOpenPostcode] = useState<boolean>(false);
  const companyInfor = useAtomValue(companyInforAtom);
  const isB2C = companyInfor.salestypeidx === 1;

  const queryClient = getClient();

  const {
    companyname,
    address,
    bizlicensenum,
    bizlicenseurl,
    bizlicensename,
    number,
    accountidx,
    bankbookname,
    bankbookurl,
    bank,
    account,
    depositor,
    usrcode,
    recommendercode,
    recommender,
    bizjoindate,
    zipcode,
    salesname,
    categoryname,
    businessname,
    bizcerttype
  } = bizInfo;

  const [isLicenseViewerOpen, setIsLicenseViewerOpen] = useState(false);
  const [isAccountViewerOpen, setIsAccountViewerOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(bizcerttype || 1);

  const openLicenseViewer = () => {
    setIsLicenseViewerOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLicenseViewer = () => {
    setIsLicenseViewerOpen(false);
    document.body.style.overflow = 'visible';
  };

  const openAccountViewer = () => {
    setIsAccountViewerOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeAccountViewer = () => {
    setIsAccountViewerOpen(false);
    document.body.style.overflow = 'visible';
  };

  const [companyName, setCompanyName] = useState(companyname);
  const [companyNumber, setCompanyNumber] = useState(number);
  const [companyLicenseNumber, setCompanyLicenseNumber] = useState(bizlicensenum);
  const [companyAddress, setCompanyAddress] = useState({ address: address, zipcode: zipcode });
  const [accountInfo, setAccountInfo] = useState({
    bank: bank,
    account: account,
    depositor: depositor
  });
  const [licenseName, setLicenseName] = useState(
    bizlicensename
      ? {
          uid: '',
          name: bizlicensename || '',
          status: '',
          percent: ''
        }
      : undefined
  );
  const [accountName, setAccountName] = useState(
    bankbookname
      ? {
          uid: '',
          name: bankbookname || '',
          status: '',
          percent: ''
        }
      : undefined
  );
  const [bizlicenseFormdata, setBizlicenseFormdata] = useState<undefined | FormData>(undefined);
  const [bankFormdata, setBankFormdata] = useState<undefined | FormData>(undefined);
  const setModalInformation = useSetAtom(modalInformationAtom);

  const companyInfo = {
    companyName: companyName,
    bizlicensenum: companyLicenseNumber,
    bizlicensename: bizlicensename,
    bizlicenseurl: bizlicenseurl,
    companyNumber: phoneAutoHyphen(companyNumber),
    address: companyAddress.address,
    zipcode: companyAddress.zipcode,
    bankAccount: null,
    bankbookurl: bankbookurl,
    bankbookname: bankbookname,
    bank: accountInfo.bank,
    accountidx: accountidx,
    account: accountInfo.account,
    depositor: accountInfo.depositor,
    code: usrcode,
    referralCode: recommendercode,
    referralName: recommender,
    date: extractDate(bizjoindate)
  };

  const [isRegistered, setIsRegistered] = useState(accountidx !== 0);

  const handleRegister = () => {
    setIsRegistered(true);
  };

  const editCompanyName = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/update/companyname',
        params: {
          companyidx: companyInfor.companyidx,
          companyname: companyName
        }
      }),

    onSuccess: () => {
      setCompanyName(companyName);
    }
  });

  const getEditCompanyName = () => {
    editCompanyName.mutate();
  };

  const editCompanyAddress = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_ADDRESS],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/update/address',
        params: {
          companyidx: companyInfor.companyidx,
          zipcode: companyAddress.zipcode,
          address: companyAddress.address
        }
      })
  });

  const getEditCompanyAddress = () => {
    editCompanyAddress.refetch();
  };

  const editCompanyNumber = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_NUMBER],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/update/number',
        params: {
          companyidx: companyInfor.companyidx,
          number: companyNumber
        }
      })
  });

  const getEditCompanyNumber = () => {
    editCompanyNumber.refetch();
  };

  const editCompanyLicenseNumber = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_LICENSENUMBER],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/update/bizlicensenum',
        params: {
          companyidx: companyInfor.companyidx,
          bizlicensenum: companyLicenseNumber,
          bizcerttype: selectedCertificate
        }
      })
  });

  const getEditCompanyLicenseNumber = () => {
    editCompanyLicenseNumber.refetch();
  };

  const editCompanyAccountInfo = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_ACCOUNT],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/set/account',
        params: {
          companyidx: companyInfor.companyidx,
          accountidx: accountidx,
          account: accountInfo.account,
          bank: accountInfo.bank,
          depositor: accountInfo.depositor
        }
      })
  });

  const editCompanyLicenseFile = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_LICENSE_IMAGE],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/update/bizlicenseurl',
        params: bizlicenseFormdata
      })
  });

  const editCompanyBankFile = useQuery({
    queryKey: [QueryKeys.EDIT_BIZ_ACCOUNT_IMAGE],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/mypage/set/bankbook',
        params: bankFormdata
      })
  });

  const getEditCompanyAccountInfo = () => {
    editCompanyAccountInfo.refetch();
  };

  const handleCopyClick = (textToCopy: string | null) => {
    if (textToCopy === null) {
      return;
    }
    navigator.clipboard.writeText(textToCopy).then(() => {
      messageApi.info({
        type: 'success',
        content: '복사되었습니다.',
        style: {
          marginTop: '10vh'
        }
      });
    });
  };

  useEffect(() => {
    if (bizlicenseFormdata && bizlicenseFormdata instanceof FormData) {
      queryClient.removeQueries({ queryKey: [QueryKeys.MYPAGEINFO] });
      editCompanyLicenseFile.refetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizlicenseFormdata]);

  useEffect(() => {
    if (bankFormdata && bankFormdata instanceof FormData) {
      queryClient.removeQueries({ queryKey: [QueryKeys.MYPAGEINFO] });
      editCompanyBankFile.refetch();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankFormdata]);

  useEffect(() => {
    const { isSuccess, data } = editCompanyLicenseFile;
    if (isSuccess && data?.result === 'success') {
      console.log(isSuccess && data);
      authInfoApi.refetch();
    }
    queryClient.removeQueries({ queryKey: [QueryKeys.EDIT_BIZ_LICENSE_IMAGE] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCompanyLicenseFile.isSuccess, editCompanyLicenseFile.data]);

  useEffect(() => {
    const { isSuccess, data } = editCompanyBankFile;
    if (isSuccess && data?.result === 'success') {
      console.log(isSuccess && data);
      authInfoApi.refetch();
    }
    queryClient.removeQueries({ queryKey: [QueryKeys.EDIT_BIZ_ACCOUNT_IMAGE] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCompanyBankFile.isSuccess, editCompanyBankFile.data]);

  const handleChangeCompanyName = (newData: string) => {
    setCompanyName(newData);
  };

  const handleChangeCompanyNumber = (newData: string) => {
    setCompanyNumber(newData);
  };

  const handleChangeCompanyLicenseNumber = (newData: string) => {
    setCompanyLicenseNumber(newData);
  };

  const handleChangeAccountInfo = (newData: any) => {
    setAccountInfo(newData);
  };

  const handleChangeAccountFile = (file: any) => {
    // setBankFile(file);
    if (file) {
      setAccountName({
        uid: file.uid,
        name: file.name,
        status: file.status,
        percent: file.percent
      });
      handleSubmitBank(file);
    } else {
      // setBankFile(null);
    }
  };

  const handleChangeLicenseFile = (file: any) => {
    // setLicenseFile(null);

    if (file) {
      setLicenseName({
        uid: file.uid,
        name: file.name,
        status: file.status,
        percent: file.percent
      });
      handleSubmitLicense(file);
    } else {
      // setLicenseFile(null);
    }
  };

  const handleChangeAddressInfo = (
    arg: React.FormEvent<HTMLInputElement> | { [key: string]: string | number }
  ) => {
    if ('target' in arg) {
      const { name, value } = (arg as React.FormEvent<HTMLInputElement>).currentTarget;
      setCompanyAddress({ ...companyAddress, [name]: value });
    } else {
      setCompanyAddress({ ...companyAddress, ...arg });
    }
    setTimeout(() => {
      getEditCompanyAddress();
    }, 0);
  };

  const handleClickMembershipWithdraw = () => {
    setModalInformation({
      type: 'WITHDRAW_MEMBER'
    });
  };

  //사업자등록증 저장시 formdata로 만들기
  const handleSubmitLicense = (file: File) => {
    const bizlicenseurlForm = new FormData();

    const param = {
      companyidx: companyInfor.companyidx,
      exfilename: licenseName?.name
    };

    bizlicenseurlForm.append('param', JSON.stringify(param));
    if (file) bizlicenseurlForm.append('bizlicenseurl', file);

    setBizlicenseFormdata(bizlicenseurlForm);
  };

  //계좌 저장시 formdata로 만들기
  const handleSubmitBank = (file: File) => {
    queryClient.removeQueries({ queryKey: [QueryKeys.EDIT_BIZ_LICENSE_IMAGE] });
    const bankurlForm = new FormData();

    const param = {
      companyidx: companyInfor.companyidx,
      accountidx: accountidx,
      exbankbookname: accountName?.name
    };

    bankurlForm.append('param', JSON.stringify(param));
    if (file) bankurlForm.append('bankbook', file);
    setBankFormdata(bankurlForm);
  };

  const handleRadioChange = (value: number) => {
    setSelectedCertificate(value);
  };

  const columns = [
    {
      dataIndex: 'userInfor',
      key: 'userInfor',
      width: '240px'
    },
    {
      dataIndex: 'contents',
      key: 'contents'
    }
  ];

  const dataSource = [
    {
      userInfor: (
        <p>
          상호<i>*</i>
        </p>
      ),
      contents: (
        <>
          <EditData
            isMypage
            text={companyInfo.companyName}
            setNewData={(newData) => handleChangeCompanyName(newData)}
            onChangeData={getEditCompanyName}
          />
        </>
      )
    },
    {
      userInfor: (
        <p>
          등록증<i>*</i>
        </p>
      ),
      contents: (
        <>
          <UploadWrapper>
            <RadioWrapper>
              <RadioButton
                value={selectedCertificate}
                options={certificateTwoData}
                onChange={handleRadioChange}
              />
            </RadioWrapper>
            <UploadInnerWrapper>
              <FileWrapper>
                <UploadInput
                  onClick={openLicenseViewer}
                  onChangeFile={handleChangeLicenseFile}
                  file={licenseName}
                  isRemove={false}
                />
              </FileWrapper>
              {isLicenseViewerOpen && companyInfo.bizlicenseurl && (
                <Viewer
                  onClose={closeLicenseViewer}
                  selectedItem={companyInfo.bizlicenseurl}
                  selectedTitle="등록증"
                  type={'image'}
                />
              )}
            </UploadInnerWrapper>
            <LicenseEdit
              text={companyInfo.bizlicensenum}
              setNewData={(newData) => handleChangeCompanyLicenseNumber(newData)}
              onChangeData={getEditCompanyLicenseNumber}
            />
          </UploadWrapper>
        </>
      )
    },
    {
      userInfor: (
        <p>
          주소<i>*</i>
        </p>
      ),
      contents: (
        <Address>
          <div>{companyInfo.address}</div>
          <DefaultButton
            size="small"
            text="우편번호"
            type="primary"
            onClick={() => setOpenPostcode(true)}
          />
          {openPostcode && (
            <AddressModal
              onClickClose={() => setOpenPostcode(false)}
              onChange={handleChangeAddressInfo}
            />
          )}
        </Address>
      )
    },
    {
      userInfor: '일반전화',
      contents: (
        <>
          {companyInfo.companyNumber !== null && (
            <EditData
              text={companyInfo.companyNumber}
              setNewData={(newData) => handleChangeCompanyNumber(newData)}
              onChangeData={getEditCompanyNumber}
            />
          )}
        </>
      )
    },
    {
      userInfor: (
        <>
          <pre>결제/환불 계좌</pre>
          <pre>(통장사본)</pre>
        </>
      ),
      contents: (
        <>
          {!isRegistered ? (
            <DefaultButton size="small" text="등록" type="primary" onClick={handleRegister} />
          ) : (
            <UploadWrapper>
              <UploadInnerWrapper>
                <FileWrapper>
                  <UploadInput
                    onClick={openAccountViewer}
                    onChangeFile={handleChangeAccountFile}
                    file={accountName}
                    isRemove={false}
                  />
                </FileWrapper>
                {isAccountViewerOpen && companyInfo.bankbookurl && (
                  <Viewer
                    onClose={closeAccountViewer}
                    selectedItem={companyInfo.bankbookurl}
                    selectedTitle="통장사본"
                    type={'image'}
                  />
                )}
              </UploadInnerWrapper>
              <AccountEdit
                accountData={accountInfo}
                setNewData={(newData) => handleChangeAccountInfo(newData)}
                onChangeData={getEditCompanyAccountInfo}
              />
            </UploadWrapper>
          )}
        </>
      )
    },
    {
      userInfor: '비즈니스 카테고리',
      contents: (
        <>
          <span>{categoryname}</span>
        </>
      )
    },
    {
      userInfor: '비즈니스 타입',
      contents: (
        <>
          <span>{salesname}</span>
        </>
      )
    },
    {
      userInfor: '계약 종류',
      contents: (
        <>
          <span>{businessname}</span>
          <p className="grayText">
            선택계약 종류 *계약 종류 변경관련 문의는 고객센터에 문의하여주세요.
          </p>
        </>
      )
    },
    {
      userInfor: '내 코드',
      contents: (
        <>
          {companyInfo.code && (
            <TableTextWrapper>
              <p>{companyInfo.code}</p>
              <button onClick={() => handleCopyClick(companyInfo.code)}>
                복사
                <IconCopy />
              </button>
            </TableTextWrapper>
          )}
        </>
      )
    },
    {
      userInfor: '추천인 코드 / 대표자명',
      contents: (
        <TableTextWrapper>
          {companyInfo.referralCode && (
            <>
              <p>{companyInfo.referralCode}</p>/<p>{companyInfo.referralName}</p>
            </>
          )}
        </TableTextWrapper>
      )
    },
    {
      userInfor: '비즈멤버십 가입일',
      contents: <div>{companyInfo.date}</div>
    },
    {
      userInfor: '엑스바디 비즈멤버십 탈퇴',
      contents: (
        <WithdrawButton>
          <DefaultButton
            size="small"
            text="탈퇴하기"
            type="primary"
            onClick={handleClickMembershipWithdraw}
          />
        </WithdrawButton>
      )
    }
  ];

  return (
    <Wrapper $isB2C={isB2C}>
      <Table
        dataSource={dataSource.map((item, index) => ({
          ...item,
          key: index
        }))}
        bordered
        columns={columns}
        pagination={false}
        showHeader={false}
      />
      {contextHolder}
    </Wrapper>
  );
};

export default BizInfo;

const Wrapper = styled.div<{ $isB2C: boolean }>`
  .ant-table-wrapper table tbody tr td:last-of-type {
    color: ${color.black2};
  }

  .ant-table-wrapper table tbody tr td p.grayText {
    color: ${color.gray500};
  }

  .ant-table-wrapper table tbody tr:nth-child(8) {
    display: ${(props) => props.$isB2C && 'none'};
  }
`;

const WithdrawButton = styled.div`
  button.ant-btn {
    height: 32px;
    ${font.caption2_medium};
  }
`;

const TableTextWrapper = styled.div`
  display: flex;
  gap: 16px;

  button {
    display: flex;
    align-items: center;
    gap: 6px;
    color: ${color.gray500};
    ${font.caption2_medium};
  }
`;

const Address = styled.div`
  display: flex;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 30px;
`;

const FileWrapper = styled.div`
  margin-bottom: 12px;

  p div {
    color: ${color.gray500};
  }

  .ant-upload-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;

    .ant-upload-list-item {
      margin-top: 0;
    }
    .ant-upload-icon {
      display: none;
    }

    .ant-upload-list {
      display: flex;
    }

    .ant-upload-list-item-name {
      color: ${color.black2} !important;
      ${font.body_light};
    }
    .ant-btn-icon {
      svg {
        fill: ${color.black2};
      }
    }
  }
`;

const UploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const UploadInnerWrapper = styled.div`
  display: flex;
`;

const RadioWrapper = styled.div`
  padding-bottom: 16px;
  border-radius: 8px;

  .ant-radio-group {
    display: flex;
    gap: 12px;

    & span {
      font-size: 14px;
      color: ${color.black2};
    }
  }
`;
